import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { patientAPI, paymentAPI, nurseAPI } from '../../services/api';
import { tokenManager } from '../../services/tokenManager';
import { CheckCircleIcon, ExclamationCircleIcon, CreditCardIcon } from '@heroicons/react/24/solid';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import SessionFeedbackDialog from '../../components/SessionFeedbackDialog';

// Helper function to get correct image URL
const getProfileImageUrl = (imagePath) => {
  if (!imagePath || typeof imagePath !== 'string') {
    return "/images/default-avatar.png";
  }
  if (imagePath.startsWith('https')) {
    return imagePath;
  }
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';
  if (imagePath.startsWith('/')) {
    return `${baseUrl}${imagePath}`;
  }
  return `${baseUrl}/${imagePath}`;
};

// Define SessionCard component before PatientDashboard
const SessionCard = ({ session, totalSessions }) => {
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [sessionStatus, setSessionStatus] = useState(session.status);
  const navigate = useNavigate();

  useEffect(() => {
    const checkSessionStatus = () => {
      const sessionDate = new Date(session.date);
      const [time, period] = session.startTime.split(' ');
      const [hours, minutes] = time.split(':');
      let sessionHours = parseInt(hours);

      if (period === 'PM' && sessionHours !== 12) {
        sessionHours += 12;
      } else if (period === 'AM' && sessionHours === 12) {
        sessionHours = 0;
      }

      sessionDate.setHours(sessionHours, parseInt(minutes), 0, 0);
      const now = new Date();

      // Calculate session end time
      const sessionEndDate = new Date(sessionDate);
      sessionEndDate.setMinutes(sessionEndDate.getMinutes() + session.durationMinutes);

      // Check if session is expired (current time is past session end time)
      const hasExpired = now > sessionEndDate;
      setIsSessionExpired(hasExpired);

      // Update session status to missed if expired and status was scheduled
      if (hasExpired && session.status === 'scheduled') {
        setSessionStatus('missed');
        // Update session status in backend
        try {
          nurseAPI.updateNurseSessionStatus(session._id, 'missed');
        } catch (error) {
          console.error('Error updating session status:', error);
        }
      }

      // Check if we're within 10 minutes of session start and before session end
      const timeDiff = sessionDate.getTime() - now.getTime();
      const minutesDiff = Math.floor(timeDiff / (1000 * 60));
      setIsSessionActive(minutesDiff <= 10 && now < sessionEndDate);
    };

    checkSessionStatus();
    const interval = setInterval(checkSessionStatus, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, [session]);

  const formatDate = () => {
    const date = new Date(session.date);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    }) + ` at ${session.startTime}`;
  };

  const getSessionTypeDisplay = () => {
    const types = {
      'monthly_checkin': 'Monthly Check-in',
      'initial_assessment': 'Initial Assessment',
      'follow_up': 'Follow-up Session',
      'weekly_checkin': 'Weekly Check-in'
    };
    return types[session.sessionType] || session.sessionType;
  };

  const handleReschedule = async () => {
    try {
      setIsProcessingPayment(true);
      const response = await paymentAPI.createReschedulePayment({
        sessionId: session._id,
        priceId: 'price_1S2maDPNYCu1z4EdiKOgxUHr',
        amount: 1000 // $10 in cents
      });

      if (response?.url) {
        window.location.href = response.url;
      } else {
        throw new Error('Failed to create payment session');
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      toast.error('Failed to process payment. Please try again.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const isJoinButtonActive = () => {
    // Check if session is missed or expired
    if (sessionStatus === 'missed' || isSessionExpired) return false;

    // Then check if session is active (within 10 minutes of start time)
    if (!isSessionActive) return false;

    // For first session, only check time
    if (totalSessions === 1) return true;

    // For sessions after first session, check monthly assessment if required
    if (session.sessionType === 'monthly_checkin') {
      return session.monthlyAssessmentCompleted;
    }

    return true;
  };

  const handleMonthlyAssessment = () => {
    window.open(`/monthly-assessment/${session._id}`, '_blank');
  };

  return (
    <div className="bg-white rounded-lg p-5 shadow-lg mb-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-gray-900">Your Next Appointment</h2>
        <span className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
          {getSessionTypeDisplay()}
        </span>
      </div>

      <div className="space-y-3">
        {/* Date and Time */}
        <div className="flex items-center text-gray-600">
          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="text-sm">{formatDate()}</span>
        </div>

        {/* Duration */}
        <div className="flex items-center text-gray-600">
          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm">{session.durationMinutes} minutes</span>
        </div>

        {/* Care Provider */}
        <div className="flex items-center text-gray-600">
          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span className="text-sm">with {session.nurseId?.name}</span>
        </div>

        {/* Session Mode */}
        <div className="flex items-center text-gray-600">
          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <span className="text-sm">{session.sessionMode} Session</span>
        </div>

        {/* Monthly Assessment Warning - Only show for non-first sessions when sendMail is true */}
        {totalSessions > 1 && 
         session.sessionType === 'monthly_checkin' && 
         session.sendMail && 
         !session.monthlyAssessmentCompleted && 
         !isSessionExpired && (
          <div className="mt-2">
            <div className="text-sm text-red-600 mb-2">
              * Monthly assessment must be completed before joining the session
            </div>
            <button
              onClick={handleMonthlyAssessment}
              className="w-full bg-blue-100 text-blue-600 py-2 px-4 rounded text-sm font-medium hover:bg-blue-200"
            >
              Complete Monthly Assessment
            </button>
          </div>
        )}

        {/* Session Status Message */}
        {sessionStatus === 'missed' && (
          <div className="mt-2 text-sm text-red-600">
            * This expired session will only be visible here for 24 hours after the missed appointment time.
          </div>
        )}

        {/* Session Timing Message */}
        {!isSessionActive && !isSessionExpired && sessionStatus === 'scheduled' && (
          <div className="mt-2 text-sm text-gray-600">
            * Join button will be active 10 minutes before the session
          </div>
        )}

        {/* Session Expired Message */}
        {/* {isSessionExpired && (
          <div className="mt-2 text-sm text-red-600">
            * This session has expired
          </div>
        )} */}

        {/* Action Buttons */}
        <div className="flex gap-3 mt-4">
          <button
            onClick={() => {
              if (session.sessionMode === 'video' && session.zoomJoinUrl) {
                window.open(session.zoomJoinUrl, '_blank', 'noopener,noreferrer');
              }
            }}
            disabled={!isJoinButtonActive()}
            className={`flex-1 py-2 px-4 rounded text-sm font-medium ${
              isJoinButtonActive()
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              {session.sessionMode === 'video' && (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                    d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
              Join {session.sessionMode} Session
            </div>
          </button>

          {(sessionStatus === 'missed' || isSessionExpired) && (
            <button
              onClick={handleReschedule}
              disabled={isProcessingPayment}
              className={`py-2 px-4 rounded text-sm font-medium ${
                isProcessingPayment
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-blue-600 border border-blue-600 hover:bg-blue-50'
              }`}
            >
              {isProcessingPayment ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </div>
              ) : (
                'Reschedule ($10)'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ProgressBar = ({ status }) => {
  const steps = [
    { label: "Paid Assessment" },
    { label: "Diagnosis Info" },
    { label: "Booked Consultation" },
    { label: "Final Diagnosis" },
  ];
  const stepStatus = [
    status.paidAssessment,
    status.diagnosisSubmitted,
    status.bookedConsultation,
    status.finalDiagnosis,
  ];

  return (
    <div className="flex justify-between items-center bg-white rounded-xl p-6 shadow">
      {steps.map((step, idx) => {
        let state = "pending";
        if (stepStatus[idx]) state = "done";
        else if (stepStatus.slice(0, idx).every(Boolean)) state = "active";
        return (
          <div key={step.label} className="flex flex-col items-center flex-1">
            <div
              className={`w-10 h-10 flex items-center justify-center rounded-full mb-2
                ${state === "done" ? "bg-blue-600 text-white" : ""}
                ${state === "active" ? "bg-blue-100 text-blue-600" : ""}
                ${state === "pending" ? "bg-gray-200 text-gray-500" : ""}
              `}
            >
              {state === "done" ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <span className="font-bold">{idx + 1}</span>
              )}
            </div>
            <span
              className={`text-sm ${state === "active" ? "text-blue-600 font-semibold" : "text-gray-700"}`}
            >
              {step.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const DiagnosisStatus = ({ diagnosisData }) => {
  const navigate = useNavigate();

  const handleViewDiagnosis = () => {
    navigate('/diagnosis');
  };

  return (
    <div className="bg-white p-6 rounded-2xl shadow-md">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-semibold text-gray-800">Diagnosis Status</h2>
        <span className="text-xs font-medium text-blue-700 bg-blue-100 px-3 py-1 rounded-full">
          {diagnosisData ? "Submitted" : "Not Started"}
        </span>
      </div>
      <p className="text-gray-600">
        {diagnosisData
          ? "Your ADHD diagnosis information has been submitted"
          : "Submit your ADHD diagnosis information to get started"}
      </p>
      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-4 mb-2">
        <div
          className="bg-blue-600 h-1.5 rounded-full"
          style={{ width: diagnosisData ? "100%" : "0%" }}
        ></div>
      </div>
      <button
        onClick={handleViewDiagnosis}
        className="text-sm font-medium text-blue-600 hover:underline cursor-pointer"
      >
        {diagnosisData ? "View Diagnosis Info →" : "Submit Diagnosis Info →"}
      </button>
    </div>
  );
};

const NurseDetails = ({ nurse, navigate, canBookSession }) => {
  if (!nurse) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-lg">
        <div className="flex items-center mb-4">
          <div className="bg-gray-100 p-2 rounded-lg mr-3">
            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Your Care Nurse</h2>
            <p className="text-sm text-gray-500">No nurse assigned yet</p>
          </div>
        </div>
        <p className="text-sm text-gray-600">
          A care nurse will be assigned to you after your diagnosis information is submitted.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-6 shadow-lg">
      <div className="flex items-center mb-4">
        <div className="bg-blue-100 p-2 rounded-lg mr-3">
          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Your Care Nurse</h2>
          <p className="text-sm text-gray-500">ADHD Care Specialist</p>
        </div>
      </div>
      <div className="flex items-center space-x-3 mb-4">
        <img
          src={getProfileImageUrl(nurse.profileImage)}
          alt={nurse.name}
          className="w-12 h-12 rounded-full bg-gray-100"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "/images/default-avatar.png";
          }}
        />
        <div>
          <p className="font-medium text-gray-900">{nurse.name}</p>
        </div>
      </div>
    </div>
  );
};

// Add Contact Information component
const ContactInformation = () => {
  return (
    <div className="bg-white rounded-lg p-6 shadow-lg">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-medium text-gray-900">Support Email</h3>
          <a 
            href="mailto:admin@adhdrapidcare.com" 
            className="text-blue-600 hover:text-blue-800 text-sm flex items-center mt-1"
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            admin@adhdrapidcare.com
          </a>
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-900">Business Hours</h3>
          <p className="text-sm text-gray-600 mt-1">24 hours/7 days</p>
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-900">Response Time</h3>
          <p className="text-sm text-gray-600 mt-1">We aim to respond to all inquiries within 24 hours during business days.</p>
        </div>
      </div>
    </div>
  );
};

const NextAppointment = ({ nextSession }) => {
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [totalSessions, setTotalSessions] = useState(0);
  const [monthlyAssessmentCompleted, setMonthlyAssessmentCompleted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkSessionExpiry = () => {
      if (!nextSession) return;

      const sessionDate = new Date(nextSession.date);
      const [time, period] = nextSession.startTime.split(' ');
      const [hours, minutes] = time.split(':');
      let sessionHours = parseInt(hours);

      if (period === 'PM' && sessionHours !== 12) {
        sessionHours += 12;
      } else if (period === 'AM' && sessionHours === 12) {
        sessionHours = 0;
      }

      sessionDate.setHours(sessionHours, parseInt(minutes), 0, 0);
      const now = new Date();

      setIsSessionExpired(now > sessionDate);
    };

    checkSessionExpiry();
    const interval = setInterval(checkSessionExpiry, 60000);
    return () => clearInterval(interval);
  }, [nextSession]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const currentUser = tokenManager.getUser();
        const userId = currentUser?._id || currentUser?.id;

        // First get patient profile to get patientId
        const profileResponse = await patientAPI.getMyProfile();
        const patientId = profileResponse?.patientProfile?._id;
        const hasCompletedAssessment = profileResponse?.monthlyAssessment?.isCompleted || false;
        setMonthlyAssessmentCompleted(hasCompletedAssessment);

        // Then get nurse sessions using patientId
        if (patientId) {
          const sessionsResponse = await nurseAPI.getPatientNurseSessions(patientId);
          if (sessionsResponse?.sessions?.length) {
            setTotalSessions(sessionsResponse.sessions.length);
          }
        }

      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  if (!nextSession) {
    return (
      <div className="bg-white rounded-lg p-5 shadow-lg">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Your Next Appointment</h2>
        <div className="text-center text-gray-500 py-4">
          No upcoming appointments scheduled
        </div>
      </div>
    );
  }

  const formatDate = () => {
    const date = new Date(nextSession.date);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    }) + ` at ${nextSession.startTime}`;
  };

  const getSessionTypeDisplay = () => {
    const types = {
      'monthly_checkin': 'Monthly Check-in',
      'initial_assessment': 'Initial Assessment',
      'follow_up': 'Follow-up Session'
    };
    return types[nextSession.sessionType] || nextSession.sessionType;
  };

  const isJoinButtonActive = () => {
    if (totalSessions === 1) return !isSessionExpired;
    return monthlyAssessmentCompleted && !isSessionExpired;
  };

  const handleReschedule = () => {
    navigate('/patient/sessions/reschedule/' + nextSession._id);
  };

  return (
    <div className="bg-white rounded-lg p-5 shadow-lg">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-gray-900">Your Next Appointment</h2>
        <span className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
          {getSessionTypeDisplay()}
        </span>
      </div>

      <div className="space-y-3">
        {/* Date and Time */}
        <div className="flex items-center text-gray-600">
          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="text-sm">{formatDate()}</span>
        </div>

        {/* Duration */}
        <div className="flex items-center text-gray-600">
          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm">{nextSession.durationMinutes} minutes</span>
        </div>

        {/* Care Provider */}
        <div className="flex items-center text-gray-600">
          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span className="text-sm">with {nextSession.nurseId?.name}</span>
        </div>

        {/* Session Mode */}
        <div className="flex items-center text-gray-600">
          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <span className="text-sm">{nextSession.sessionMode} Session</span>
        </div>

        {/* Monthly Assessment Warning */}
        {totalSessions > 1 && !monthlyAssessmentCompleted && !isSessionExpired && (
          <div className="mt-2 text-sm text-red-600">
            * Monthly assessment must be completed before joining the session
          </div>
        )}

        {/* Session Expired Message */}
        {/* {isSessionExpired && (
          <div className="mt-2 text-sm text-red-600">
            * This session has expired
          </div>
        )} */}

        {/* Action Buttons */}
        <div className="flex gap-3 mt-4">
          <button
            // onClick={() => navigate('/patient/sessions')}
            disabled={!isJoinButtonActive()}
            className={`py-2 px-4 rounded text-sm font-medium ${isJoinButtonActive()
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
          >
            Join {nextSession.sessionMode} Session
          </button>
          <button
            onClick={handleReschedule}
            className="text-gray-600 hover:text-gray-700 px-4 py-2 text-sm font-medium"
          >
            Reschedule
          </button>
        </div>
      </div>
    </div>
  );
};

const RecentNotifications = ({ notifications }) => {
  if (!notifications || notifications.length === 0) {
    return (
      <div className="bg-white p-6 rounded-2xl shadow-sm">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Recent Notifications</h2>
        <p className="text-gray-500 text-center">No notifications yet</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-2xl shadow-sm">
      <h2 className="text-lg font-semibold text-gray-800 mb-4">Recent Notifications</h2>
      <div className="space-y-4">
        {notifications.map((notification, index) => (
          <div key={index} className="flex items-start space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">{notification.title}</p>
              <p className="text-xs text-gray-500">{notification.message}</p>
              <p className="text-xs text-gray-400 mt-1">{notification.time}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const OfficialReceipts = ({ receipts }) => {
  if (!receipts || receipts.length === 0) {
    return null;
  }

  return (
    <div className="bg-white p-6 rounded-2xl shadow-md">
      <h2 className="text-lg font-semibold text-gray-800 mb-4">Official Receipts</h2>
      <div className="space-y-4">
        {receipts.map((receipt) => (
          <div key={receipt._id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-4">
              <div className="bg-blue-100 p-2 rounded-lg">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-gray-900">{receipt.receiptNumber}</p>
                <p className="text-sm text-gray-500">{new Date(receipt.serviceDate).toLocaleDateString()}</p>
              </div>
            </div>
            <a
              href={receipt.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 font-medium text-sm"
            >
              Download →
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};

const LatestPayment = ({ payment }) => {
  if (!payment) {
    return (
      <div className="bg-gray-50 mt-5 p-6 rounded-2xl shadow-sm border border-dashed border-gray-300 text-center">
        <ExclamationCircleIcon className="w-10 h-10 text-gray-400 mx-auto mb-2" />
        <h3 className="text-md font-semibold text-gray-700">No payments yet</h3>
        <p className="text-sm text-gray-500 mt-1">
          Your latest payment will appear here once it's completed.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 mt-5 rounded-2xl shadow-sm">
      <h2 className="text-lg font-semibold text-gray-800 mb-4">Latest Payment</h2>
      <div className="flex items-start">
        <div className="bg-gray-100 p-2 rounded-lg mr-4">
          <CreditCardIcon className="w-6 h-6 text-gray-500" />
        </div>
        <div className="flex-1">
          <div className="flex justify-between items-center">
            <p className="font-medium text-gray-800">Assessment Payment</p>
            <p className="font-bold text-gray-900">
              {(payment.amountCents / 100).toLocaleString(undefined, {
                style: 'currency',
                currency: payment.currency ? payment.currency.toUpperCase() : 'AUD'
              })}
            </p>
          </div>
          <p className="text-sm text-gray-500">
            {new Date(payment.createdAt).toLocaleDateString()}
          </p>
        </div>
      </div>
      <div className="border-t border-gray-200 mt-4 pt-4 flex justify-between items-center text-sm">
        <span className="text-gray-600">Status</span>
        <span className="font-medium text-gray-800">{payment.status}</span>
      </div>
    </div>
  );
};

const SubscriptionCard = ({ payment, loading, patientProfile }) => {
  const [isCanceling, setIsCanceling] = useState(false);

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? This will stop future billing but you will still have access until the end of your current billing period.')) {
      return;
    }

    try {
      setIsCanceling(true);
      await paymentAPI.cancelSubscription();
      toast.success('Your subscription has been cancelled and will end at the end of the current billing period.');
      window.location.reload(); // Refresh to show updated status
    } catch (error) {
      console.error('Error canceling subscription:', error);
      toast.error(error.response?.data?.error || 'Failed to cancel subscription. Please try again.');
    } finally {
      setIsCanceling(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-lg animate-pulse">
        <div className="h-5 w-24 bg-gray-200 rounded mb-6"></div>
        <div className="space-y-4">
          <div>
            <div className="h-4 w-16 bg-gray-200 rounded mb-2"></div>
            <div className="h-5 w-48 bg-gray-200 rounded"></div>
          </div>
          <div>
            <div className="h-4 w-16 bg-gray-200 rounded mb-2"></div>
            <div className="h-6 w-16 bg-gray-200 rounded"></div>
          </div>
          <div>
            <div className="h-4 w-24 bg-gray-200 rounded mb-2"></div>
            <div className="h-5 w-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  // Calculate next billing date based on subscription type and last payment
  const getNextBillingDate = () => {
    if (!payment?.completedAt) return 'Not available';

    const lastPaymentDate = new Date(payment.completedAt);
    const today = new Date();
    const subscriptionType = payment.metadata?.subscriptionType || 'monthly_subscription';
    const isWeekly = subscriptionType === 'weekly_subscription';

    // Calculate next billing date based on subscription type
    let nextBillingDate = new Date(lastPaymentDate);

    if (isWeekly) {
      // For weekly subscription
      while (nextBillingDate <= today) {
        nextBillingDate.setDate(nextBillingDate.getDate() + 7);
      }
    } else {
      // For monthly subscription
      while (nextBillingDate <= today) {
        nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
      }
    }

    // Format the date
    const formattedDate = nextBillingDate.toLocaleString('default', {
      day: 'numeric',
      month: 'long'
    });

    // Calculate days remaining
    const daysRemaining = Math.ceil((nextBillingDate - today) / (1000 * 60 * 60 * 24));

    // Return different messages based on days remaining
    if (daysRemaining === 0) {
      return 'Renews today';
    } else if (daysRemaining === 1) {
      return 'Renews tomorrow';
    } else {
      return `Renews ${formattedDate}`;
    }
  };

  // Format the amount with currency
  const formatAmount = (amountCents) => {
    if (!amountCents) return '$0';
    return `$${(amountCents / 100).toFixed(2)}`;
  };

  const getPlanDetails = () => {
    if (!payment) return 'No active plan';
    const amount = formatAmount(payment.amountCents);
    const subscriptionType = payment.metadata?.subscriptionType || 'monthly_subscription';
    const interval = subscriptionType === 'weekly_subscription' ? 'week' : 'month';
    return `ADHD Care Plan - ${amount}/${interval}`;
  };

  // Get subscription status from patient profile
  const getSubscriptionStatus = () => {
    const status = patientProfile?.subscription_status || 'pending';

    switch (status.toLowerCase()) {
      case 'active':
        return { text: 'Active', style: 'bg-green-100 text-green-800' };
      case 'pending':
        return { text: 'Pending', style: 'bg-yellow-100 text-yellow-800' };
      case 'paused':
        return { text: 'Paused', style: 'bg-yellow-100 text-yellow-800' };
      case 'cancelled':
        return { text: 'Cancelled', style: 'bg-gray-100 text-gray-800' };
      default:
        return {
          text: status.charAt(0).toUpperCase() + status.slice(1),
          style: 'bg-gray-100 text-gray-800'
        };
    }
  };

  const status = getSubscriptionStatus();

  // Show cancel button only for active subscriptions
  const showCancelButton = ['active', 'paused'].includes(status.text?.toLowerCase());

  return (
    <div className="bg-white rounded-lg p-6 shadow-lg">
      <h2 className="text-lg font-semibold text-gray-900 mb-6">Subscription</h2>
      <div className="space-y-4">
        {/* Plan */}
        <div>
          <p className="text-sm text-gray-500 mb-1">Plan</p>
          <p className="text-gray-900 font-medium">{getPlanDetails()}</p>
        </div>

        {/* Status */}
        <div>
          <p className="text-sm text-gray-500 mb-1">Status</p>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.style}`}>
            {status.text}
          </span>
        </div>

        {/* Next billing date */}
        <div>
          <p className="text-sm text-gray-500 mb-1">Next billing date</p>
          <p className="text-gray-900">{getNextBillingDate()}</p>
        </div>

        {/* Cancel Subscription Button - Only show for active subscriptions */}
        {showCancelButton && (
          <div className="pt-2">
            <button
              onClick={handleCancelSubscription}
              disabled={isCanceling}
              className="w-full flex items-center justify-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
            >
              {isCanceling ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-red-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Canceling...
                </>
              ) : (
                'Cancel Subscription'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const WeeklyCheckIn = () => {
  return (
    <div className="bg-white rounded-lg p-5 shadow-lg">
      <h2 className="text-lg font-medium text-gray-900 mb-4">Weekly Check-in</h2>
      <div className="space-y-4">
        {/* Progress */}
        <div>
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Progress</span>
            <span>4/7 completed</span>
          </div>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 rounded-full"
              style={{ width: '57%' }} // 4/7 ≈ 57%
            />
          </div>
        </div>

        {/* Action Button */}
        <button className="w-full bg-blue-600 text-white py-2 px-4 rounded text-sm font-medium hover:bg-blue-700 transition-colors">
          Complete This Week's Check-in
        </button>
      </div>
    </div>
  );
};

const CheckInHistory = () => {
  const weeks = [
    { week: 1, status: 'Completed', score: '8/10' },
    { week: 2, status: 'Completed', score: '8/10' },
    { week: 3, status: 'Completed', score: '8/10' },
    { week: 4, status: 'Completed', score: '8/10' },
    { week: 5, status: 'Completed', score: '8/10' },
    { week: 6, status: 'Completed', score: '8/10' },
    { week: 7, status: 'Pending', score: '-' },
    { week: 8, status: 'Pending', score: '-' }
  ];

  return (
    <div className="bg-white rounded-lg p-6 shadow-lg">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-medium text-gray-900">Check-in History</h2>
        <span className="text-sm text-gray-500">6 of 8 weeks completed</span>
      </div>

      <div className="space-y-4">
        {weeks.map((week) => (
          <div key={week.week} className="flex items-center justify-between">
            <div className="flex items-center">
              {week.status === 'Completed' ? (
                <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <circle cx="10" cy="10" r="10" />
                  <path fill="white" d="M14.293 6.293a1 1 0 0 1 1.414 1.414l-6 6a1 1 0 0 1-1.414 0l-3-3a1 1 0 1 1 1.414-1.414L8 10.586l5.293-4.293z" />
                </svg>
              ) : (
                <div className="w-5 h-5 rounded-full border-2 border-gray-200 mr-3"></div>
              )}
              <span className="text-sm text-gray-700">
                Week {week.week} - {week.status}
              </span>
            </div>
            <span className="text-sm text-gray-500">
              {week.status === 'Completed' ? `Score: ${week.score}` : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const MonthlyAssessmentAlert = () => {
  return (
    <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4 mt-6">
      <div className="flex items-start space-x-3">
        {/* Warning Icon */}
        <div className="flex-shrink-0 mt-0.5">
          <svg className="w-5 h-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>

        <div className="flex-1">
          {/* Title */}
          <p className="text-sm font-medium text-yellow-600 mb-1">
            Your monthly assessment is ready
          </p>
          {/* Description */}
          <p className="text-sm text-yellow-600">
            Please complete it before your nurse session. Takes ~15 minutes. Saves automatically.
          </p>

          {/* Button */}
          <button className="mt-3 bg-yellow-500 text-white px-4 py-2 rounded text-sm font-medium hover:bg-amber-300 transition-colors">
            Start Assessment
          </button>
        </div>
      </div>
    </div>
  );
};

const DashboardSkeleton = () => (
  <div>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2 space-y-8">
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <div className="h-8 bg-gray-200 rounded w-32 animate-pulse mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-48 animate-pulse"></div>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-sm">
          <div className="h-6 bg-gray-200 rounded w-48 animate-pulse mb-4"></div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
          </div>
        </div>
      </div>
      <div className="space-y-8">
        <div className="bg-white p-6 rounded-2xl shadow-sm">
          <div className="h-6 bg-gray-200 rounded w-32 animate-pulse mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6 animate-pulse"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ProgressSteps = ({ profile }) => {
  const steps = [
    {
      label: "Subscribed",
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      isActive: profile?.subscription_status === 'active'
    },
    {
      label: "Onboarding",
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
      isActive: profile?.care_plan_onboarding_completed === true
    },
    {
      label: "Nurse Session",
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      ),
      isActive: profile && 'assigned_nurse_id' in profile && profile.assigned_nurse_id !== null
    },
    {
      label: "Monitoring",
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      isActive: profile?.subscription_status === 'active'
    }
  ];

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          // Check if this step should be highlighted (active or next in line)
          const previousStepsActive = index === 0 || steps.slice(0, index).every(s => s.isActive);
          const isHighlighted = step.isActive || (previousStepsActive && !step.isActive);

          return (
            <React.Fragment key={step.label}>
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step.isActive ? 'bg-blue-600 text-white' :
                    previousStepsActive ? 'bg-blue-100 text-blue-600' : 'bg-gray-200'
                  }`}>
                  {step.isActive ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <span className="text-xs font-medium">{index + 1}</span>
                  )}
                </div>
                <span className={`text-xs mt-2 ${isHighlighted ? 'text-blue-600 font-semibold' : 'text-gray-500'
                  }`}>
                  {step.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className="flex-1 h-1 mx-2 bg-gray-200">
                  <div className={`h-full bg-blue-600 transition-all ${step.isActive ? 'w-full' : 'w-0'
                    }`} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

const SessionFeedbackCard = ({ session, onSubmit, onSkip }) => {
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      await patientAPI.submitSessionFeedback(session._id, {
        rating,
        feedback
      });
      onSubmit?.();
    } catch (err) {
      console.error('Error submitting feedback:', err);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Format date to "Friday, August 1"
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-lg">
      <div className="flex items-center gap-3 mb-3">
        <div className="text-yellow-400">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-gray-800">Session Review</h2>
      </div>
      <p className="text-gray-600 mb-6">
        How was your session on <span className="text-gray-700">{formatDate(session.date)}</span> at <span className="text-gray-700">{session.startTime}</span>?
      </p>

      {/* Star Rating */}
      <div className="flex justify-center gap-2 mb-6">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => setRating(star)}
            className="focus:outline-none"
          >
            <svg 
              className={`w-8 h-8 ${rating >= star ? 'text-yellow-400' : 'text-gray-200'}`}
              fill="currentColor" 
              viewBox="0 0 24 24"
            >
              <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
          </button>
        ))}
      </div>

      {/* Feedback Text Area */}
      <div className="mb-6">
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows="4"
          placeholder="Share your thoughts about the session (optional)"
        />
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          onClick={handleSubmit}
          disabled={loading || rating === 0}
          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium ${
            loading || rating === 0
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {loading ? 'Submitting...' : 'Submit'}
        </button>
        <button
          onClick={onSkip}
          disabled={loading}
          className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
        >
          Skip
        </button>
      </div>
    </div>
  );
};

const PatientDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState({
    paidAssessment: false,
    diagnosisSubmitted: false,
    bookedConsultation: false,
    finalDiagnosis: false,
  });
  const [diagnosisData, setDiagnosisData] = useState(null);
  const [assignedNurse, setAssignedNurse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [patientProfile, setPatientProfile] = useState(null);
  const [nextSession, setNextSession] = useState(null);
  const [officialReceipts, setOfficialReceipts] = useState([]);
  const [latestPayment, setLatestPayment] = useState(null);
  const [payment, setPayment] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);

  // Add new state for latest completed session
  const [latestCompletedSession, setLatestCompletedSession] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const currentUser = tokenManager.getUser();
        const userId = currentUser?._id || currentUser?.id;

        // Fetch patient profile
        try {
          const profileResponse = await patientAPI.getMyProfile();
          setPatientProfile(profileResponse);
          const patientId = profileResponse.patientProfile._id;

          if (profileResponse.diagnosisHistory) {
            setDiagnosisData(profileResponse.diagnosisHistory);
            setStatus(prev => ({ ...prev, diagnosisSubmitted: true }));
          }

          // Get assigned nurse
          try {
            const nurseResponse = await patientAPI.getAssignedNurse(userId);
            if (nurseResponse.success) {
              setAssignedNurse(nurseResponse.nurse);
            }
          } catch (e) {
            console.error('Error fetching nurse details:', e);
          }

          // Fetch nurse sessions using patientId
          try {
            const response = await nurseAPI.getPatientNurseSessions(patientId);
            console.log('Sessions response:', response);

            if (response?.sessions && Array.isArray(response.sessions)) {
              // Find latest completed session without feedback
              const completedSession = response.sessions.find(session => 
                session.status === 'completed' && 
                (!session.feedback || !session.feedback.submittedAt)
              );
              setLatestCompletedSession(completedSession);

              // Filter sessions and handle missed sessions within 24 hours
              const now = new Date();
              const twentyFourHoursAgo = new Date(now.getTime() - (24 * 60 * 60 * 1000));

              const activeSessions = response.sessions
                .filter(session => {
                  // If session is missed, check if it's within 24 hours
                  if (session.status === 'missed') {
                    const sessionDate = new Date(session.date);
                    const [time, period] = session.endTime.split(' ');
                    const [hours, minutes] = time.split(':');
                    let sessionHours = parseInt(hours);
                    
                    if (period === 'PM' && sessionHours !== 12) sessionHours += 12;
                    else if (period === 'AM' && sessionHours === 12) sessionHours = 0;
                    
                    sessionDate.setHours(sessionHours, parseInt(minutes), 0, 0);
                    return sessionDate > twentyFourHoursAgo;
                  }
                  // Show all non-completed sessions
                  return session.status !== 'completed';
                })
                .sort((a, b) => {
                  const dateA = new Date(a.date);
                  const timeA = a.startTime.split(' ')[0].split(':');
                  const periodA = a.startTime.split(' ')[1];
                  let hoursA = parseInt(timeA[0]);
                  if (periodA === 'PM' && hoursA !== 12) hoursA += 12;
                  else if (periodA === 'AM' && hoursA === 12) hoursA = 0;
                  dateA.setHours(hoursA, parseInt(timeA[1]), 0, 0);

                  const dateB = new Date(b.date);
                  const timeB = b.startTime.split(' ')[0].split(':');
                  const periodB = b.startTime.split(' ')[1];
                  let hoursB = parseInt(timeB[0]);
                  if (periodB === 'PM' && hoursB !== 12) hoursB += 12;
                  else if (periodB === 'AM' && hoursB === 12) hoursB = 0;
                  dateB.setHours(hoursB, parseInt(timeB[1]), 0, 0);

                  return dateA - dateB;
                });

              setSessions(activeSessions);
            }
          } catch (e) {
            console.error('Error fetching nurse sessions:', e);
            setSessions([]);
          }

        } catch (e) {
          console.error('Error fetching patient profile:', e);
        }

        // Check payment status
        try {
          const payment = await paymentAPI.getLatestPaymentForPatient(userId);
          if (payment && payment.status === 'completed') {
            setStatus(prev => ({ ...prev, paidAssessment: true }));
          }
          setLatestPayment(payment);
        } catch (e) {
          console.error('Error fetching payment:', e);
          setLatestPayment(null);
        }

        // Get notifications
        try {
          const notificationsResponse = await patientAPI.getNotifications(userId);
          setNotifications(notificationsResponse?.notifications || []);
        } catch (e) {
          console.error('Error fetching notifications:', e);
          setNotifications([]);
        }

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Define steps array
  const getSteps = (profile) => [
    {
      label: "Subscribed",
      icon: (
        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      isActive: profile?.subscription_status === 'active'
    },
    {
      label: "Onboarding Completed",
      icon: (
        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
      isActive: profile?.care_plan_onboarding_completed === true
    },
    {
      label: "First Nurse Session",
      icon: (
        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      ),
      isActive: profile && 'assigned_nurse_id' in profile && profile.assigned_nurse_id !== null
    },
    {
      label: "Active Monitoring",
      icon: (
        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      isActive: profile?.subscription_status === 'active'
    }
  ];

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoadingProfile(true);
        const response = await patientAPI.getMyProfile();
        if (response?.patientProfile) {
          setPatientProfile(response.patientProfile);
          if (response.patientProfile.assigned_nurse_id) {
            setAssignedNurse(response.patientProfile.assigned_nurse_id);
          }
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const currentUser = tokenManager.getUser();
        const userId = currentUser?._id || currentUser?.id;

        // Get patient profile
        try {
          const profileResponse = await patientAPI.getMyProfile();
          setPatientProfile(profileResponse?.patientProfile);

          // Update status based on profile data
          if (profileResponse?.patientProfile) {
            const profile = profileResponse.patientProfile;
            
            // Update onboarding status
            status.diagnosisSubmitted = profile.care_plan_onboarding_completed || false;
            
            // Update assigned nurse
            if (profile.assigned_nurse_id) {
              setAssignedNurse(profile.assigned_nurse_id);
            }
          }
        } catch (e) {
          console.error('Error fetching patient profile:', e);
        }

        // Check payment status
        let paidAssessment = false;
        try {
          const payment = await paymentAPI.getLatestPaymentForPatient(userId);
          if (payment && payment.status === 'completed') {
            paidAssessment = true;
            status.paidAssessment = true;
          }
          setLatestPayment(payment);
        } catch (e) {
          console.error('Error fetching payment:', e);
          setLatestPayment(null);
        }

        // Get nurse sessions
        try {
          const patientId = patientProfile?._id;
          if (patientId) {
            const response = await patientAPI.getSessions(patientId);
            if (response && response.length > 0) {
              setSessions(response);
              // Find the next scheduled session
              const nextSession = response.find(s =>
                s.status === 'scheduled' && new Date(s.date) > new Date()
              );
              if (nextSession) {
                setNextSession(nextSession);
              }

              // Update booking status
              status.bookedConsultation = response.some(s => s.status !== 'cancelled');
            }
          }
        } catch (e) {
          console.error('Error fetching nurse sessions:', e);
        }

        // Get notifications
        try {
          const notificationsResponse = await patientAPI.getNotifications(userId);
          if (notificationsResponse) {
            setNotifications(notificationsResponse);
          }
        } catch (e) {
          console.error('Error fetching notifications:', e);
          setNotifications([]);
        }

        // Update final status
        setStatus({ 
          ...status, 
          paidAssessment,
          diagnosisSubmitted: status.diagnosisSubmitted || false,
          bookedConsultation: status.bookedConsultation || false
        });

      } catch (error) {
        console.error('Error fetching status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, []);

  if (loading || loadingProfile) return <DashboardSkeleton />;

  return (
    <div className="">
      {/* Welcome Header */}
      <div className="mb-8">
       <h1> Welcome back, {patientProfile?.name
          ? patientProfile.name.split(' ')[0].charAt(0).toUpperCase() + patientProfile.name.split(' ')[0].slice(1).toLowerCase()
          : 'Patient'}
      </h1>
        <p className="text-gray-600 text-sm">
          Here's your care overview
        </p>
        <div className="h-px bg-gray-200 mt-4"></div>
      </div>


      {/* Progress Steps */}
      <ProgressSteps profile={patientProfile} />

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-6">
        <div className="lg:col-span-2 space-y-6">
          {sessions.length > 0 ? (
            sessions.map(session => (
              <SessionCard
                key={session._id}
                session={session}
                totalSessions={sessions.length}
              />
            ))
          ) : (
            <div className="bg-white rounded-lg p-5 shadow-lg">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Your Next Appointment</h2>
              <div className="text-center text-gray-500 py-4">
                No upcoming appointments scheduled
              </div>
            </div>
          )}
          {/* <CheckInHistory /> */}
          <OfficialReceipts receipts={officialReceipts} />
        </div>

        <div className="space-y-6">
          <SubscriptionCard payment={latestPayment} loading={loading} patientProfile={patientProfile} />
          
          <NurseDetails
            nurse={assignedNurse}
            navigate={navigate}
            canBookSession={status.diagnosisSubmitted}
          />

          <ContactInformation />
          {latestCompletedSession && (
            <SessionFeedbackCard
              session={latestCompletedSession}
              onSubmit={() => {
                setLatestCompletedSession(null);
              }}
              onSkip={() => {
                setLatestCompletedSession(null);
              }}
            />
          )}
          {/* <WeeklyCheckIn /> */}
        </div>
      </div>

      {/* Full Width Alert at Bottom */}
      {/* <MonthlyAssessmentAlert /> */}

      {/* Feedback Dialog */}
      {/* {showFeedbackDialog && (
        <SessionFeedbackDialog
          sessionId={latestCompletedSession?._id}
          sessionDate={latestCompletedSession?.date}
          sessionTime={latestCompletedSession?.endTime}
          onClose={() => setShowFeedbackDialog(false)}
          onSubmit={() => {
            // Remove the session from feedback after submitting
            setLatestCompletedSession(null);
            setShowFeedbackDialog(false);
          }}
        />
      )} */}
    </div>
  );
};

export default PatientDashboard;
